/**
 * media-color-sync.js (custom, NeverGraduate)
 *
 * Reverse of Horizon's "variant -> media" behaviour: when a shopper clicks a
 * product image (main slide, desktop thumbnail or mobile pagination dot), select
 * the matching Color option in the main variant picker so Horizon's normal
 * variant flow runs (price, add-to-cart variant id, ?variant= URL, gallery).
 *
 * The media id -> color value map is rendered by snippets/media-color-sync.liquid
 * as <script type="application/json" data-media-color-map> inside <media-gallery>.
 *
 * Notes
 * - Only explicit clicks sync. Arrow / swipe navigation does NOT, because Horizon
 *   re-renders the gallery with the selected variant's image first, which would
 *   make sequential navigation ping-pong between colors.
 * - After Horizon swaps in the re-rendered gallery we re-select the image the
 *   shopper clicked (e.g. a back view) instead of jumping to the front image.
 * - Main-image clicks still open the zoom dialog. The color change is applied
 *   once the dialog is open, and the new gallery's zoom dialog is re-opened on
 *   the same image in the same task, so the zoom stays open.
 */

const MAP_SELECTOR = 'script[type="application/json"][data-media-color-map]';
const CLICK_MOVE_TOLERANCE = 10; // px; larger pointer travel = drag/swipe, ignore
const PENDING_TTL = 8000; // ms to wait for Horizon's variant:update after we select
const ZOOM_WAIT_MAX = 1500; // ms to wait for the zoom dialog to open
const ZOOM_SETTLE = 450; // ms for the zoom open view transition to finish

const mapCache = new WeakMap();
let pointerStart = null;
/** @type {null | {mediaId: string, section: Element, reopenZoom: boolean, at: number}} */
let pending = null;
let zoomTimer = 0;

/** @param {Element} gallery */
function getMap(gallery) {
  const script = gallery.querySelector(MAP_SELECTOR);
  if (!script) return null;
  if (mapCache.has(script)) return mapCache.get(script);
  let data = null;
  try {
    data = JSON.parse(script.textContent || '');
    if (!data || typeof data.map !== 'object' || typeof data.optionIndex !== 'number') data = null;
  } catch (error) {
    console.warn('[media-color-sync] invalid map', error);
  }
  mapCache.set(script, data);
  return data;
}

/** Main-product variant picker for this gallery (never product cards / quick add). */
function findPicker(gallery, data) {
  const scope = gallery.closest('.shopify-section, dialog') || document;
  const pickers = scope.querySelectorAll('variant-picker');
  for (const picker of pickers) {
    if (!(picker instanceof HTMLElement)) continue;
    if (picker.closest('product-card, quick-add-dialog, quick-add-component')) continue;
    if (picker.dataset.templateProductMatch !== 'true') continue;
    if (data.productId && picker.dataset.productId && picker.dataset.productId !== String(data.productId)) continue;
    return picker;
  }
  return null;
}

function mediaIdFromSlide(slide) {
  const media = slide?.querySelector('[data-media-id]');
  return media ? media.getAttribute('data-media-id') : null;
}

function mainSlides(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  if (!slideshow) return [];
  const refSlides = slideshow.refs?.slides;
  if (Array.isArray(refSlides) && refSlides.length) return refSlides;
  return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter((s) => !s.closest('zoom-dialog'));
}

/**
 * Select `value` on the Color option through Horizon's own picker.
 * @returns {boolean} true if a change was dispatched
 */
function selectColor(picker, optionIndex, value) {
  const optionEls = picker.querySelectorAll('.variant-option');
  const optionEl = optionEls[optionIndex];
  if (!optionEl) return false;

  const select = optionEl.querySelector('select');
  if (select instanceof HTMLSelectElement) {
    if (select.value === value) return false;
    const option = Array.from(select.options).find((o) => o.value === value);
    if (!option) return false;
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  const radios = Array.from(optionEl.querySelectorAll('input[type="radio"]'));
  const radio = radios.find((r) => r instanceof HTMLInputElement && r.value === value);
  if (!(radio instanceof HTMLInputElement) || radio.checked) return false;
  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

function syncFromMedia(gallery, mediaId, { reopenZoom = false } = {}) {
  if (!mediaId) return;
  const data = getMap(gallery);
  if (!data) return;
  const value = data.map[mediaId];
  if (!value) return;
  const picker = findPicker(gallery, data);
  if (!picker) return;
  const section = gallery.closest('.shopify-section, dialog') || document.body;
  pending = { mediaId: String(mediaId), section, reopenZoom, at: Date.now() };
  if (!selectColor(picker, data.optionIndex, value)) pending = null;
}

function zoomDialogOf(gallery) {
  const zoom = gallery.querySelector('zoom-dialog');
  const dialog = zoom?.querySelector('dialog');
  return { zoom, dialog };
}

/** Wait (bounded) for the zoom dialog to be open and its open transition done. */
function afterZoomOpens(gallery, callback) {
  clearTimeout(zoomTimer);
  const { dialog } = zoomDialogOf(gallery);
  if (!dialog) return callback();
  const started = Date.now();
  const tick = () => {
    if (!gallery.isConnected) return;
    if (dialog.open) {
      zoomTimer = window.setTimeout(callback, ZOOM_SETTLE);
    } else if (Date.now() - started > ZOOM_WAIT_MAX) {
      callback();
    } else {
      zoomTimer = window.setTimeout(tick, 50);
    }
  };
  tick();
}

document.addEventListener(
  'pointerdown',
  (event) => {
    pointerStart = { x: event.clientX, y: event.clientY };
  },
  { capture: true, passive: true }
);

// Window capture phase: runs before Horizon's delegated on:click handlers, which may
// re-order slides (infinite slideshow placeholder trick) before bubble listeners run.
window.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const gallery = target.closest('media-gallery');
  if (!gallery || !gallery.querySelector(MAP_SELECTOR)) return;
  if (target.closest('zoom-dialog')) return; // clicks inside the open zoom dialog

  if (pointerStart && event.detail !== 0) {
    const dx = Math.abs(event.clientX - pointerStart.x);
    const dy = Math.abs(event.clientY - pointerStart.y);
    if (dx > CLICK_MOVE_TOLERANCE || dy > CLICK_MOVE_TOLERANCE) return; // drag / swipe
  }

  // Thumbnail or pagination dot: on:click="/select/N" (resolved before Horizon acts)
  const control = target.closest('slideshow-controls [on\\:click*="/select/"]');
  if (control) {
    const match = (control.getAttribute('on:click') || '').match(/\/select\/(\d+)/);
    if (!match) return;
    const slide = mainSlides(gallery)[Number(match[1])];
    const mediaId = mediaIdFromSlide(slide);
    // Let Horizon's own select() run first, then switch the variant.
    setTimeout(() => syncFromMedia(gallery, mediaId), 0);
    return;
  }

  if (target.closest('slideshow-arrows, slideshow-controls, button, a, deferred-media, product-model, model-viewer, video, iframe')) return;

  // Main slide (carousel) or grid item
  const slide = target.closest('slideshow-slide, li');
  const mediaId = mediaIdFromSlide(slide);
  if (!mediaId) return;

  const zoomable = slide.matches('.product-media-container--zoomable') || slide.hasAttribute('on:click');
  if (zoomable && zoomDialogOf(gallery).dialog) {
    afterZoomOpens(gallery, () => {
      if (!gallery.isConnected) return;
      const { dialog } = zoomDialogOf(gallery);
      syncFromMedia(gallery, mediaId, { reopenZoom: !!dialog?.open });
    });
  } else {
    setTimeout(() => syncFromMedia(gallery, mediaId), 0);
  }
}, true);

// Capture phase: runs before Horizon's <media-gallery> replaces itself.
document.addEventListener(
  'variant:update',
  (event) => {
    if (!pending) return;
    if (Date.now() - pending.at > PENDING_TTL) {
      pending = null;
      return;
    }
    const oldGallery = pending.section.querySelector('media-gallery');
    const { dialog } = oldGallery ? zoomDialogOf(oldGallery) : { dialog: null };
    pending.reopenZoom = !!(pending.reopenZoom && dialog?.open && !dialog.classList.contains('dialog--closed'));

    // Pre-select the clicked image in the incoming gallery markup so Horizon's
    // slideshow initialises on it (avoids a flash of the variant's front image).
    try {
      const html = event.detail?.data?.html;
      const incoming = html?.querySelector?.('media-gallery');
      const slideshow = incoming?.querySelector('slideshow-component');
      if (!slideshow) return;
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide')).filter((el) => !el.closest('zoom-dialog'));
      const index = slides.findIndex((slide) => mediaIdFromSlide(slide) === pending.mediaId);
      if (index > 0) {
        if (!slides[index].hasAttribute('slide-id')) slides[index].setAttribute('slide-id', `media-${pending.mediaId}`);
        slideshow.setAttribute('initial-slide', String(index));
      }
    } catch (error) {
      console.warn('[media-color-sync] could not preselect slide', error);
    }
  },
  true
);

// Bubble phase: runs after Horizon swapped in the new <media-gallery>.
document.addEventListener('variant:update', () => {
  if (!pending) return;
  const { mediaId, section, reopenZoom } = pending;
  pending = null;
  const gallery = section.querySelector('media-gallery');
  if (!gallery) return;

  const slides = mainSlides(gallery);
  const index = slides.findIndex((slide) => mediaIdFromSlide(slide) === mediaId);
  if (index < 0) return;

  if (reopenZoom) {
    const { zoom } = zoomDialogOf(gallery);
    if (zoom && typeof zoom.open === 'function') {
      try {
        zoom.open(index, { preventDefault() {}, target: null });
        if (typeof zoom.selectThumbnail === 'function') zoom.selectThumbnail(index, { behavior: 'instant' });
      } catch (error) {
        console.warn('[media-color-sync] could not reopen zoom', error);
      }
    }
  }

  if (index === 0) return; // Horizon already shows the selected variant's image first
  restoreSlide(gallery, index);
});

/** Re-select the clicked image in the freshly rendered slideshow (setup is async). */
function restoreSlide(gallery, index) {
  const slideshow = gallery.querySelector('slideshow-component');
  if (!slideshow || typeof slideshow.select !== 'function') return;
  const slides = mainSlides(gallery);
  const scroller = slideshow.querySelector('slideshow-slides');
  const slide = slides[index];
  // Synchronously position the scroller before the next paint.
  if (scroller && slide) {
    const s = scroller.getBoundingClientRect();
    const t = slide.getBoundingClientRect();
    scroller.scrollTo({ left: scroller.scrollLeft + (t.left - s.left), top: scroller.scrollTop + (t.top - s.top), behavior: 'instant' });
  }
  const started = Date.now();
  let interacted = false;
  const stop = () => (interacted = true);
  for (const type of ['pointerdown', 'touchstart', 'keydown', 'click', 'wheel']) {
    gallery.addEventListener(type, stop, { once: true, capture: true, passive: true });
  }
  // Safety net: keep the clicked image selected while Horizon finishes its own
  // async slideshow setup, then stop.
  const attempt = () => {
    if (!slideshow.isConnected || interacted || Date.now() - started > 1200) return;
    if (slideshow.current !== index) slideshow.select(index, undefined, { animate: false });
    window.setTimeout(attempt, 60);
  };
  requestAnimationFrame(() => requestAnimationFrame(attempt));
}
